/**
 * Age gate utility (SOS-357)
 *
 * Pure helpers for validating a date of birth and persisting an under-18
 * sign-up block on the device. Block is a UX deterrent against reload bypass,
 * not a security control — clearing local data or using a different
 * browser/device is an accepted bypass for this version.
 */

const BLOCK_KEY = 'sossiety_age_gate_blocked';
const MIN_AGE = 18;

export type DateValidationError = 'empty' | 'invalid' | 'future' | null;

const isYmd = (value: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(value);

const parseYmd = (value: string): Date | null => {
  if (!isYmd(value)) return null;
  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  if (
    date.getFullYear() !== y ||
    date.getMonth() !== m - 1 ||
    date.getDate() !== d
  ) {
    return null;
  }
  return date;
};

export const validateDateOfBirth = (value: string): DateValidationError => {
  if (!value || !value.trim()) return 'empty';
  const date = parseYmd(value);
  if (!date) return 'invalid';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (date.getTime() > today.getTime()) return 'future';
  return null;
};

export const calculateAge = (value: string, reference: Date = new Date()): number => {
  const dob = parseYmd(value);
  if (!dob) return NaN;
  let age = reference.getFullYear() - dob.getFullYear();
  const monthDiff = reference.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && reference.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age;
};

export const meetsMinimumAge = (value: string, reference: Date = new Date()): boolean =>
  calculateAge(value, reference) >= MIN_AGE;

/** Today's date as "yyyy-MM-dd" — used as the `max` bound for date-of-birth inputs. */
export const todayYmd = (reference: Date = new Date()): string => {
  const yyyy = reference.getFullYear();
  const mm = String(reference.getMonth() + 1).padStart(2, '0');
  const dd = String(reference.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export const isAgeBlocked = (): boolean => {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(BLOCK_KEY) === '1';
  } catch {
    return false;
  }
};

export const setAgeBlocked = (): void => {
  try {
    window.localStorage.setItem(BLOCK_KEY, '1');
  } catch {
    // best-effort persistence; private mode / quota errors are silent
  }
};

export const clearAgeBlocked = (): void => {
  try {
    window.localStorage.removeItem(BLOCK_KEY);
  } catch {
    // ignore
  }
};
